import {
  CustomEvent,
  CustomEventsRole,
  DateString,
  EventChange,
  EventChangeField,
  fullDayMonthDateString,
  timeStringFromDate,
  toDateString,
  User,
} from '@nextforce/api'
import { makeStyles, Text } from '@rneui/themed'
import { t } from 'app/i18l'
import { isEmpty } from 'lodash'
import React, { useCallback, useMemo } from 'react'
import { SectionList, View } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { UserAvatar } from 'shared/components/user/user_avatar'

interface Props {
  event?: CustomEvent
  createdBy?: User
}

interface Section {
  date: DateString
  data: EventChange[]
  index: number
}

export function EventChangesView({ event }: Props) {
  const styles = useStyles()
  const changes = useMemo(() => event?.eventChanges ?? [], [event])
  const sections: Section[] = useMemo(
    () =>
      Object.entries(
        changes.reduce((acc, change) => {
          const date = toDateString(change.changedAt)
          acc[date] = acc[date] || []
          acc[date].push(change)
          return acc
        }, {} as { [key: string]: EventChange[] })
      ).map(([date, data], index) => ({
        date,
        data,
        index,
      })),
    [changes]
  )

  const renderItem = useCallback(
    ({ item }: { item: EventChange }) => {
      return (
        <View style={styles.userChangeItem}>
          <UserAvatar
            user={item.changedBy}
            size={'small'}
            showAvatarIfNoPhone
          />
          <View style={styles.changeDescriptionContainer}>
            <ChangeDescriptionView item={item} />
          </View>
          <Text style={styles.time}>{timeStringFromDate(item.changedAt)}</Text>
        </View>
      )
    },
    [styles.changeDescriptionContainer, styles.userChangeItem, styles.time]
  )

  return (
    <SafeAreaView edges={['left', 'right', 'bottom']} style={styles.root}>
      <View style={styles.container}>
        <SectionList
          sections={sections}
          ListHeaderComponent={
            <CreatedBy user={event?.createdBy} date={event?.createdAt} />
          }
          renderSectionHeader={({ section: { date } }) => (
            <View style={styles.dateHeaderContainer}>
              <Text style={styles.dateHeader}>
                {fullDayMonthDateString(date)}
              </Text>
            </View>
          )}
          renderItem={renderItem}
        />
      </View>
    </SafeAreaView>
  )
}

function ChangeDescriptionView({ item }: { item: EventChange }) {
  const styles = useStyles()
  switch (item.type) {
    case 'edit':
      const EditedFields = () => {
        if (!item.changedTo) {
          return null
        }
        const EditedFieldItems = Object.keys(item.changedTo).map(field => {
          if (item.changedTo) {
            if (!isEmpty(item.changedTo[field as EventChangeField])) {
              switch (field as EventChangeField) {
                case 'startDate':
                  return (
                    <Text style={styles.bulletText}>{`• ${t('startDate')} ${t(
                      'changed'
                    )} ${t('to')} ${item.changedTo.startDate}.`}</Text>
                  )
                case 'endDate':
                  return (
                    <Text style={styles.bulletText}>{`• ${t('endDate')} ${t(
                      'changed'
                    )} ${t('to')} ${item.changedTo.endDate}.`}</Text>
                  )
                case 'visibleToRoles':
                  return (
                    <Text style={styles.bulletText}>{`• ${t('visibility')} ${t(
                      'changed'
                    )} ${t('to')} ${getVisibilityText(
                      item.changedTo.visibleToRoles as CustomEventsRole
                    )}.`}</Text>
                  )
                case 'blockRto':
                  return (
                    <Text style={styles.bulletText}>{`• ${t('disallowRto')} ${t(
                      'changed'
                    )} ${t('to')} ${item.changedTo.blockRto}.`}</Text>
                  )
                case 'summary':
                  return (
                    <Text style={styles.bulletText}>{`• ${t('summary')} ${t(
                      'changed'
                    )} ${t('to')} '${item.changedTo.summary}'.`}</Text>
                  )
                case 'description':
                  return (
                    <Text style={styles.bulletText}>{`• ${t('description')} ${t(
                      'changed'
                    )} ${t('to')} '${item.changedTo.description}'.`}</Text>
                  )
                case 'addedToStores':
                  return (
                    <Text style={styles.bulletText}>{`• ${t('added')} ${t(
                      'to'
                    )} ${t('stores')} ${item.changedTo.addedToStores}.`}</Text>
                  )
                case 'removedFromStores':
                  return (
                    <Text style={styles.bulletText}>{`• ${t('removed')} ${t(
                      'from'
                    )} ${t('stores')} ${
                      item.changedTo.removedFromStores
                    }.`}</Text>
                  )
                default:
                  return <></>
              }
            }
          }
          return null
        })
        return <>{EditedFieldItems}</>
      }
      return (
        <View>
          <Text>
            {`${t('madeEditText', {
              name: `${item.changedBy.firstName} ${item.changedBy?.lastName}`,
              count: Object.keys(item.changedTo ?? {}).length,
            }).toLowerCase()}`}
          </Text>
          <EditedFields />
        </View>
      )
  }
}

function CreatedBy({ user, date }: { user?: User; date?: DateString }) {
  const styles = useStyles()

  if (!user || !date) {
    return null
  }

  return (
    <View
      style={[
        styles.userChangeItem,
        { alignItems: 'center', padding: 10, margin: 10 },
      ]}
    >
      <UserAvatar user={user} size={'small'} showAvatarIfNoPhone />
      <View style={styles.changeDescriptionContainer}>
        <Text>{`${t('eventCreatedBy', {
          name: `${user.firstName} ${user.lastName}`,
          date: fullDayMonthDateString(date),
        })}`}</Text>
      </View>
    </View>
  )
}

function getVisibilityText(visibleToRoles: CustomEventsRole): string {
  switch (visibleToRoles) {
    case 'OBSERVER':
      return t('managersAndAbove')
    case 'OBSERVER_MANAGER':
      return t('managementTeam')
    case 'ALL':
      return t('everyone')
    default:
      return ''
  }
}

const useStyles = makeStyles(theme => ({
  root: {
    flex: 1,
  },
  container: {
    flex: 1,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  bulletText: {
    fontSize: 14,
    lineHeight: 16,
  },
  dateHeader: {
    fontSize: 14,
    backgroundColor: theme.colors.grey7,
    paddingVertical: 5,
    paddingHorizontal: 10,
    borderRadius: 5,
  },
  dateHeaderContainer: {
    alignItems: 'center',
    marginVertical: 10,
  },
  time: {
    alignSelf: 'flex-start',
    fontSize: 12,
    backgroundColor: theme.colors.grey7,
    borderRadius: 15,
    paddingVertical: 2,
    width: 70,
    textAlign: 'center',
    marginLeft: 5,
  },
  changeDescriptionContainer: {
    flex: 1,
    paddingLeft: 10,
  },
  userChangeItem: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: 10,
  },
}))
